<template>
  <div class="auth-page">
    <div class="auth-brand">
      <img
        src="/icons/icon.svg"
        alt=""
        class="auth-mark"
        width="56"
        height="56"
      >
      <p class="kicker">{{ t('nav.brand') }}</p>
    </div>
    <h1>{{ title }}</h1>
    <p class="lead">{{ lead }}</p>
    <slot />
    <slot name="footer" />
    <div class="discord-row">
      <DiscordJoinButton />
      <SupportButton />
    </div>
    <div class="auth-lang">
      <LanguageSelect />
    </div>
  </div>
</template>

<script setup lang="ts">
import LanguageSelect from './LanguageSelect.vue'
import DiscordJoinButton from './DiscordJoinButton.vue'
import SupportButton from './SupportButton.vue'
import { useI18n } from '../i18n/t'

defineProps<{
  title: string
  lead: string
}>()

const { t } = useI18n()
</script>

<style scoped>
.auth-page {
  width: min(440px, 100%);
  margin: 0 auto;
  padding: 48px 16px 64px;
  font-family: Roboto, system-ui, sans-serif;
  color: var(--md-sys-color-on-surface);
}

.auth-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.auth-mark {
  width: 56px;
  height: 56px;
  border-radius: 14px;
}

.kicker {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0 0 8px;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
}

.lead {
  margin: 0 0 24px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.auth-page :deep(.panel) {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.auth-page :deep(.field) {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--md-sys-color-on-surface-variant);
}

.auth-page :deep(.field input) {
  min-height: 44px;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
  font-weight: 400;
}

.auth-page :deep(.field input:focus) {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.auth-page :deep(.reveal-field input) {
  padding-inline-end: 44px;
}

.auth-page :deep(.cta) {
  min-height: 44px;
  border: 0;
  border-radius: 8px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.auth-page :deep(.cta:disabled) {
  opacity: 0.6;
  cursor: wait;
}

.auth-page :deep(.error) {
  margin: 0;
  color: #e07070;
  font-size: 14px;
  line-height: 20px;
}

.auth-page :deep(.switch) {
  margin: 20px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  text-align: center;
}

.auth-page :deep(.switch a) {
  color: var(--md-sys-color-primary);
  font-weight: 600;
  text-decoration: none;
}

.discord-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
}

.auth-lang {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}
</style>
